import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "/api";
const CSRF_COOKIE_NAME = "csrftoken";
const CSRF_HEADER_NAME = "X-CSRFToken";

const axiosConfig = {
  baseURL: API_URL,
  withCredentials: true,
  xsrfCookieName: CSRF_COOKIE_NAME,
  xsrfHeaderName: CSRF_HEADER_NAME,
};

const apiClient = axios.create(axiosConfig);
const refreshClient = axios.create(axiosConfig);

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const REFRESH_EXCLUDED_PATHS = [
  "/csrf/",
  "/login/",
  "/logout/",
  "/refresh/",
  "/register/",
];

let refreshPromise: Promise<void> | null = null;
let authFailureHandler: (() => void) | undefined;

export const setAuthFailureHandler = (handler?: () => void) => {
  authFailureHandler = handler;
};

const isRefreshExcluded = (url?: string) => {
  const path = url?.split("?", 1)[0] || "";
  return REFRESH_EXCLUDED_PATHS.some((excludedPath) =>
    path.endsWith(excludedPath)
  );
};

const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = refreshClient
      .post("/refresh/")
      .then(() => undefined)
      .catch((error) => {
        authFailureHandler?.();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetryableRequestConfig | undefined;

    if (
      error.response?.status !== 401 ||
      !config ||
      isRefreshExcluded(config.url)
    ) {
      return Promise.reject(error);
    }

    if (config._retry) {
      authFailureHandler?.();
      return Promise.reject(error);
    }

    config._retry = true;

    try {
      await refreshAccessToken();
      return apiClient.request(config);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
);

const apiRequest = <ReqType, ResType>(
  method: "get" | "post" | "put" | "patch" | "delete",
  url: string,
  data?: ReqType,
  params?: Record<string, any>
): Promise<AxiosResponse<ResType>> => {
  return apiClient.request<ResType>({
    method,
    url,
    data: data ?? null,
    params: params ?? undefined,
  });
};

export default apiRequest;
